<template>
  <div class="d-flex fill-height flex-column">
    <ActionBar
      class="pa-3"
      border-bottom
      :disabled="isLocked"
      show-add
      @add="toggleDialog()"
      show-save
      @save="saveChanges()"
      show-import
      @import="toggleImportDialog(true)"
      :disabled-import="schematisationStagesToImport.length === 0"
    />
    <div class="d-flex flex-column overflow-y-auto">
      <NoDataAvailableCenter v-if="!soilProfileBlock?.length" />
      <v-expansion-panels v-model="indexOpen" multiple variant="accordion" v-else>
        <v-expansion-panel
          v-for="(itemProfile, index) in soilProfileBlock"
          :key="itemProfile.id"
          :value="itemProfile.id"
          @click="setPanel(itemProfile.id)"
        >
          <v-divider v-if="index !== 0"></v-divider>
          <v-expansion-panel-title
            @contextmenu.prevent="isLocked || showActionMenu($event, itemProfile)"
          >
            <SoilProfileBlockHeader
              :endIndex="soilProfileBlock.length - 1"
              :index="index"
              :item-profile="itemProfile"
            />
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <SoilProfileBlockParameter
              ref="soilProfileParameter"
              :index="index"
              :item-profile="itemProfile"
              :readonly="isLocked"
            />
            <SoilProfileBlockTable
              ref="soilProfileTable"
              :profileId="itemProfile.id"
              :items="getSoilProfilePointById(itemProfile.id)"
              :groundLevel="calculateZCoordinate(itemProfile.x)"
              :soil-type="soilType"
              :readonly="isLocked"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <TableActionMenu
      @update:show-menu="showMenu = $event"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      :add-button="false"
      @action="deleteAndGenerate(selectedItem)"
    />
    <ImportFromPreviousStageDialog
      :schematisation-stages="schematisationStagesToImport"
      v-model="importDialog"
      :loading="importLoading"
      text="soil profile blocks"
      @close="toggleImportDialog(false)"
      @import="handleImport"
    />
    <SoilProfileBlockDialog></SoilProfileBlockDialog>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DirtyFieldsMixIn from '@/mixins/DirtyFieldsMixIn.vue'
import ShowActionMenuMixin from '@/mixins/ShowActionMenuMixin.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import SoilProfileBlockTable from '@/components/soilcomposition/SoilProfileBlockTable.vue'
import SoilProfileBlockHeader from '@/components/soilcomposition/SoilProfileBlockHeader.vue'
import SoilProfileBlockParameter from '@/components/soilcomposition/SoilProfileBlockParameter.vue'
import { lineIntersect, lineString } from '@turf/turf'
import ImportFromPreviousStageDialog from '@/components/schematisationDetail/ImportFromPreviousStageDialog.vue'
import SoilProfileBlockDialog from '@/components/soilcomposition/SoilProfileBlockDialog.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import NoDataAvailableCenter from '@/components/soilcomposition/NoDataAvailableCenter.vue'

export default {
  name: 'SoilProfileBlockPanels',
  components: {
    NoDataAvailableCenter,
    ActionBar,
    ImportFromPreviousStageDialog,
    SoilProfileBlockParameter,
    SoilProfileBlockHeader,
    SoilProfileBlockTable,
    TableActionMenu,
    SoilProfileBlockDialog
  },
  mixins: [DirtyFieldsMixIn, ShowActionMenuMixin],
  data: () => {
    return {
      indexOpen: [],
      panel: null,
      headers: [
        { text: '#', value: '' },
        { text: 'Soil type', value: 'soil_type' },
        { text: 'MV', value: 'z' }
      ]
    }
  },
  computed: {
    ...mapState('stabSchematisationStage', {
      currentStabSchematisationStage: (state) => state.currentItem
    }),
    ...mapState('topoProfilePoint', {
      items: (state) => state.items
    }),
    ...mapState('soilProfile', {
      soilProfileDirtyItems: (state) => state.dirtyItems,
      importLoading: (state) => state.importLoading,
      importDialog: (state) => state.importDialog
    }),
    ...mapState('soilProfilePoint', {
      soilProfilePointItems: (state) => state.items
    }),
    ...mapState('baseSoilType', {
      soilType: (state) => state.items
    }),
    ...mapGetters('stabSchematisationStage', {
      soilCompositionId: 'currentCompositionId'
    }),
    ...mapGetters('topoProfilePoint', {
      topoProfileGeometry: 'getTopoProfileGeometry'
    }),
    ...mapGetters('soilProfile', {
      soilProfileBlock: 'getSortedSoilProfile'
    }),
    ...mapGetters('soilProfilePoint', {
      getSoilProfilePointById: 'getSoilProfilePointById'
    }),
    ...mapGetters('topoProfilePoint', {
      plotDataMaxY: 'getPlotDataMaxY'
    }),
    ...mapGetters('stabSchematisation', {
      isLocked: 'isLockedCurrent'
    }),
    ...mapGetters('stabSchematisationStage', {
      schematisationStagesToImport: 'previousSchematisationStagesHaveSoilProfile'
    })
  },
  watch: {
    panel() {
      if (!this.indexOpen.includes(this.panel)) {
        this.panel = null
      }
      if (this.panel) {
        const item = this.soilProfileBlock.find((item) => {
          return item.id === this.panel
        })
        this.setCurrentItem(item)
      } else {
        this.setCurrentItem(null)
      }
    }
  },
  methods: {
    ...mapActions('soilProfile', {
      setCurrentItem: 'setCurrentItem',
      setNestedParameter: 'setNestedParameter',
      updateSoilProfile: 'update',
      emptySoilProfileDirtyItems: 'emptyDirtyItems',
      deleteSoilProfileItem: 'deleteItem',
      toggleWriteLoading: 'toggleWriteLoading',
      toggleImportDialog: 'toggleImportDialog',
      importSoilProfilesFromStage: 'importSoilProfilesFromStage',
      toggleDialog: 'toggleDialog'
    }),
    ...mapActions('soilProfilePoint', {
      updateSoilProfilePoint: 'update',
      createSoilProfilePoint: 'create',
      emptySoilProfilePointDirtyItems: 'emptyDirtyItems',
      deleteSoilProfilePointItem: 'deleteItems'
    }),
    ...mapActions('soilLayer', {
      generateSoilLayer: 'generate'
    }),
    ...mapActions('statePoints', {
      fetchStatePoints: 'fetchItems'
    }),
    async deleteAndGenerate(selectedItem) {
      await this.deleteSoilProfilePointBySoilProfile(selectedItem)
      await this.deleteSoilProfile(selectedItem)
      let generate = this.soilProfilePointItems.length || !this.soilProfileBlock.length
      if (generate) {
        await this.getSoilLayer()
      }
    },
    setPanel(id) {
      this.panel = this.indexOpen.includes(id) ? id : null
    },
    showActionMenu($event, itemProfile) {
      if (!this.indexOpen.length) {
        this.show($event, itemProfile)
      }
    },
    calculateZCoordinate(x) {
      if (!isFinite(this.plotDataMaxY())) {
        return null
      }
      let intersectLineCenter = [
        [x, -50],
        [x, this.plotDataMaxY()]
      ]
      const line1 = lineString(this.topoProfileGeometry())
      const intersects = lineIntersect(line1, lineString(intersectLineCenter))
      return intersects.features.length
        ? Math.round(intersects.features[0].geometry.coordinates[1] * 100) / 100
        : null
    },
    async deleteSoilProfile(soilProfileItem) {
      let index = this.soilProfileBlock.findIndex((item) => {
        return item.id === soilProfileItem.id
      })
      let item = null,
        parameter,
        value
      const previousSoilProfile = this.soilProfileBlock[index - 1]
      const nextSoilProfile = this.soilProfileBlock[index + 1]
      if (index === 0 && nextSoilProfile) {
        item = nextSoilProfile
        parameter = 'x_start'
        value = null
      }
      if (index === this.soilProfileBlock.length - 1 && previousSoilProfile) {
        item = previousSoilProfile
        parameter = 'x_end'
        value = null
      }
      if (previousSoilProfile && nextSoilProfile) {
        item = nextSoilProfile
        parameter = 'x_start'
        value = previousSoilProfile['x_end']
      }
      if (item && parameter) {
        this.setNestedParameter({
          value: value,
          item: item,
          parameter: parameter
        })
      }
      await this.deleteSoilProfileItem(soilProfileItem.id)
      await this.updateSoilProfile()
    },
    async deleteSoilProfilePointBySoilProfile(selectedItem) {
      await this.deleteSoilProfilePointItem(this.getSoilProfilePointById(selectedItem.id))
    },
    cleanUpPanel() {
      if (this.soilProfileDirtyItems.length) {
        this.indexOpen = []
        this.panel = null
      }
    },
    async saveChanges() {
      try {
        this.cleanUpPanel()
        await this.updateSoilProfile()
        await this.updateSoilProfilePoint()
        await this.createSoilProfilePoint()
        this.emptySoilProfilePointDirtyItems()
        this.emptySoilProfileDirtyItems()
        if (this.soilProfilePointItems.length) {
          await this.getSoilLayer()
        }
        if (this.$refs['soilProfileParameter']) {
          this.$refs['soilProfileParameter'].forEach((item) => {
            item.emptyDirtyItemsClass()
          })
        }
        if (this.$refs['soilProfileTable']) {
          this.$refs['soilProfileTable'].forEach((item) => {
            item.emptyDirtyItemsClass()
          })
        }
      } catch (e) {
        this.toggleWriteLoading()
      }
    },
    async getSoilLayer() {
      try {
        await this.generateSoilLayer(this.soilCompositionId)
        await this.fetchStatePoints(this.soilCompositionId)
      } catch (e) {
        console.log(e)
      }
    },
    async handleImport(sourceSchematisationStageId) {
      try {
        this.cleanUpPanel()
        this.emptySoilProfilePointDirtyItems()
        this.emptySoilProfileDirtyItems()
        await this.importSoilProfilesFromStage({
          sourceSchematisationStageId,
          targetSchematisationStageId: this.currentStabSchematisationStage.id,
          soilCompositionId: this.soilCompositionId
        })
        await this.getSoilLayer()
      } catch (e) {
        console.log(e)
      } finally {
        this.toggleImportDialog(false)
      }
    }
  }
}
</script>
